import React, { useEffect, useRef, useState } from "react";
import { bulkUpdateUnits, fetchUnitLabels } from "../../api/OrderItemUnits";
import { renderAlertMessage } from "../../helpers/common";
import { useItemsMeasureUnitsContext } from "./ItemsMeasureUnits";
import { openSelectOnEnter } from "./selectKeyboard";
import { toDecimalInput, toIntegerInput } from "./itemDraftHelpers";

// Numeric bulk-apply fields reject alphabets/symbols as typed: piece_split is
// a whole count; weight and L/W/H are decimals. Everything else (uom selects,
// location) passes through untouched.
const DECIMAL_FIELDS = new Set(["weight", "length", "width", "height"]);
const INTEGER_FIELDS = new Set(["piece_split"]);

const STATUS_LIST = [
  "Pending",
  "Received",
  "Loaded",
  "In Transit",
  "On Hand",
  "Delivered",
  "Exception",
];

const EMPTY_DRAFT = {
  piece_split: "",
  weight: "",
  weight_uom: "lb",
  length: "",
  width: "",
  height: "",
  dim_uom: "in",
  location: "",
  status: "",
};

// Same booked_*/reweigh_* column pairing as UnitRow's private modeFields —
// duplicated (not imported) since UnitRow doesn't export it and Task 5 isn't
// meant to touch that file. The bulk row always edits whichever pair of
// columns the table's current Booked/Re-Weighed toggle is showing.
function modeFields(mode) {
  return mode === "reweigh"
    ? {
        weight: "reweigh_weight",
        weightUom: "reweigh_weight_uom",
        length: "reweigh_length",
        width: "reweigh_width",
        height: "reweigh_height",
        dimUom: "reweigh_dim_uom",
      }
    : {
        weight: "booked_weight",
        weightUom: "booked_weight_uom",
        length: "booked_length",
        width: "booked_width",
        height: "booked_height",
        dimUom: "booked_dim_uom",
      };
}

// Sparse-by-construction: a field only lands in the payload when the user
// actually typed into it, so applying (say) only a status change never
// clobbers weight/dims/location on every checked unit. Mirrors the mockup's
// bulk-apply handler, which only assigns fields whose bulk* draft is
// non-blank.
//
// QA fix wave 2 / F1: piece split is never locked out of the bulk apply —
// same as the per-row input, a typed split always lands in the payload.
function buildSparseAttrs(draft, mode) {
  const f = modeFields(mode);
  const attrs = {};

  const location = (draft.location || "").trim();
  if (location !== "") attrs.storage_location = location;

  if (draft.status !== "") attrs.status = draft.status;

  const weight = (draft.weight || "").trim();
  if (weight !== "") {
    attrs[f.weight] = weight;
    attrs[f.weightUom] = draft.weight_uom || "lb";
  }

  const split = (draft.piece_split || "").trim();
  if (split !== "") attrs.piece_split = split;

  const length = (draft.length || "").trim();
  const width = (draft.width || "").trim();
  const height = (draft.height || "").trim();
  if (length !== "" || width !== "" || height !== "") {
    if (length !== "") attrs[f.length] = length;
    if (width !== "") attrs[f.width] = width;
    if (height !== "") attrs[f.height] = height;
    attrs[f.dimUom] = draft.dim_uom || "in";
  }

  return attrs;
}

// labels_pdf (base64) -> a real file download without a second round trip.
function downloadLabelsPdf(base64, fileName) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "labels.pdf";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// Rendered by UnitsTable (and, in Task 6, FlatUnitsView) as the first <tr> of
// the scan-table's tbody whenever selection is non-empty (mockup .bulk-row).
// `showItemColumn` adds one blank <td> (mockup's flat bulk-row has an empty
// cell under the Item header) so this row's cells still line up under
// FlatUnitsView's one extra column instead of drifting left of
// Status/kebab.
//
// `readOnly` is belt-and-braces (Task 10): both mount sites already refuse
// to render this row when the box is locked — inline-edit "Done" can flip
// readOnly back to true mid-mount — but the write handlers below also
// early-return so no surviving click path can PATCH a locked box.
//
// QA fix wave 2 / F1: piece split is never locked here either (previously
// gated on the item's HUs/pieces via a `preview`/`item` prop pair this row
// no longer needs — the server still enforces each unit's own manual-flag
// rule regardless of what's checked in bulk).
const BulkApplyRow = ({
  checkedIds,
  mode,
  onApplied,
  onClear,
  showItemColumn = false,
  readOnly = false,
  // Draft-until-Save: when the box is editable, a bulk apply routes each
  // selected unit's edit into the item's pending buckets (same model as a
  // per-cell edit) instead of PATCHing immediately — so it shows the unsaved
  // border and flushes on Save via bulk_upsert. buildSparseAttrs is
  // mode-scoped (reweigh_* in Re-Weighed mode), so a Re-Weighed apply never
  // writes booked fields.
  deferSaves = false,
  onUnitPendingEdit,
}) => {
  const {
    reload,
    // Config-based label printing (same rule as the per-unit Print label):
    // print_label_based_on_lpn ON → download selected units' LPN labels
    // (existing); OFF → open the order-level label-template popup.
    orgSettings: ctxOrgSettings = {},
    openOrderLabelPopup = () => {},
  } = useItemsMeasureUnitsContext();
  const lpnLabelMode =
    ctxOrgSettings &&
    ctxOrgSettings.items_measure_units_screen === "true" &&
    ctxOrgSettings.print_label_based_on_lpn === "true";
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [applying, setApplying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // IM5: this same instance stays mounted across a Booked <-> Re-Weigh mode
  // toggle (the selection is unchanged), so a value typed under one mode would
  // otherwise survive and get applied to the OTHER mode's field on the next
  // Apply (buildSparseAttrs is mode-scoped). Clear the staged draft on any mode
  // change so a Booked entry can't be written as reweigh_weight, or vice versa.
  useEffect(() => {
    setDraft(EMPTY_DRAFT);
  }, [mode]);

  const ids = checkedIds ? Array.from(checkedIds) : [];
  const count = ids.length;

  const handleFieldChange = (field) => (e) => {
    let value = e.target.value;
    if (DECIMAL_FIELDS.has(field)) value = toDecimalInput(value);
    else if (INTEGER_FIELDS.has(field)) value = toIntegerInput(value);
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleClear = () => {
    setDraft(EMPTY_DRAFT);
    if (onClear) onClear();
  };

  const handleDownloadLabels = () => {
    if (downloading || count === 0) return;
    // Config OFF: no per-unit LPN labels — open the order-level label-template
    // popup (order-details "Print Labels") for the whole order instead.
    if (!lpnLabelMode) {
      openOrderLabelPopup();
      return;
    }
    setDownloading(true);
    fetchUnitLabels(ids)
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          downloadLabelsPdf(result.labels_pdf, result.file_name);
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) setDownloading(false);
      });
  };

  const handleApply = () => {
    if (readOnly || applying || count === 0) return;
    const attrs = buildSparseAttrs(draft, mode);
    if (Object.keys(attrs).length === 0) return; // nothing typed to apply

    // Deferred path (editable box): stage the same mode-scoped attrs onto every
    // selected unit's pending "existing" bucket. Marks the card dirty (border)
    // and flushes on Save; a Re-Weighed apply carries only reweigh_* keys, so
    // booked is never touched.
    if (deferSaves && typeof onUnitPendingEdit === "function") {
      ids.forEach((unitId) => onUnitPendingEdit("existing", unitId, attrs));
      setDraft(EMPTY_DRAFT);
      if (onClear) onClear();
      return;
    }

    setApplying(true);
    bulkUpdateUnits(ids, attrs)
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          setDraft(EMPTY_DRAFT);
          if (onClear) onClear();
          reload();
          if (onApplied) onApplied(result.units);
        } else {
          // Whole-batch reject (invalid field / cross-order mix, per the
          // bulk_update contract): keep the selection AND the typed values
          // so the user can fix and retry instead of starting over.
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) setApplying(false);
      });
  };

  return (
    <tr className="bulk-row">
      <td />
      <td className="bulk-count-cell">
        <div className="bulk-count-line">
          <span className="bulk-count">{count} selected</span>
          <button type="button" className="bulk-clear" onClick={handleClear}>
            Clear
          </button>
        </div>
        <button
          type="button"
          className="bulk-clear"
          onClick={handleDownloadLabels}
          disabled={downloading}
        >
          Download labels
        </button>
      </td>
      {showItemColumn && <td />}
      {/* Read-only view (selecting units in view mode is allowed only to
          bulk-download labels, not to edit): the per-field edit inputs and the
          Apply button are omitted, leaving empty cells so the count/Download
          area still lines up under the table columns. */}
      <td>
        {!readOnly && (
          <input
            type="text"
            inputMode="numeric"
            className="split-input"
            placeholder="&ndash;"
            value={draft.piece_split}
            onChange={handleFieldChange("piece_split")}
          />
        )}
      </td>
      <td>
        {!readOnly && (
          <div className="unit-input">
            <input
              type="text"
              inputMode="decimal"
              placeholder="Weight"
              value={draft.weight}
              onChange={handleFieldChange("weight")}
            />
            <select value={draft.weight_uom} onChange={handleFieldChange("weight_uom")} onKeyDown={openSelectOnEnter}>
              {["lb", "kg"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        )}
      </td>
      <td>
        {!readOnly && (
          <div className="dims-cell">
            <input
              type="text"
              inputMode="decimal"
              placeholder="L"
              value={draft.length}
              onChange={handleFieldChange("length")}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="W"
              value={draft.width}
              onChange={handleFieldChange("width")}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="H"
              value={draft.height}
              onChange={handleFieldChange("height")}
            />
            <select value={draft.dim_uom} onChange={handleFieldChange("dim_uom")} onKeyDown={openSelectOnEnter}>
              {["in", "cm"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        )}
      </td>
      <td>
        {!readOnly && (
          <input
            type="text"
            className="loc-input"
            placeholder="Storage location"
            value={draft.location}
            onChange={handleFieldChange("location")}
          />
        )}
      </td>
      {/* Status column hidden (product decision) — no bulk status cell. */}
      <td>
        {!readOnly && (
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply} disabled={applying}>
            Apply
          </button>
        )}
      </td>
    </tr>
  );
};

export default BulkApplyRow;
